import React from 'react';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LetterTile from './LetterTile';
import ModifierCard from './ModifierCard';
import { LETTER_POINTS } from './config/gameConfig';
import { useScrabbleGameContext } from './context/ScrabbleGameContext';

const getBoardColor = (t) => t.palette.primary.main;
const getGameColor = (t) => t.palette.accent?.main ?? '#d97706';
const getBagColor = (t) => t.palette.secondary.main;

function StatCard({ Icon, count, label, getColor }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.2,
        borderRadius: 2,
        px: { xs: 1.2, sm: 1.5 },
        py: 1,
        backgroundColor: (t) => alpha(getColor(t), 0.07),
        border: (t) => `1px solid ${alpha(getColor(t), 0.2)}`,
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          backgroundColor: (t) => alpha(getColor(t), 0.15),
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18, color: (t) => getColor(t) }} />
      </Box>
      <Stack spacing={0}>
        <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1 }}>{count}</Typography>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Box>
  );
}

function SectionPanel({ Icon, title, count, getColor, children }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: { xs: 1.2, sm: 1.5 },
          py: 1,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          background: (t) =>
            `linear-gradient(135deg, ${alpha(getColor(t), 0.09)} 0%, ${alpha(t.palette.background.paper, 0.97)} 65%)`,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.2,
            backgroundColor: (t) => alpha(getColor(t), 0.18),
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16, color: (t) => getColor(t) }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 13, flex: 1 }}>{title}</Typography>
        <Chip
          size="small"
          label={count}
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 0.8,
            backgroundColor: (t) => alpha(getColor(t), 0.12),
            color: (t) => getColor(t),
            border: (t) => `1px solid ${alpha(getColor(t), 0.3)}`,
          }}
        />
      </Stack>
      <Box sx={{ p: { xs: 1.2, sm: 1.5 } }}>{children}</Box>
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: (t) => `1px dashed ${alpha(t.palette.text.secondary, 0.25)}`,
        py: 2.5,
        px: 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.8,
        textAlign: 'center',
      }}
    >
      <InboxRoundedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{message}</Typography>
    </Box>
  );
}

function ModifierPanel({ onPlaceModifier, onInspectTile }) {
  const {
    heldBoardModifiers = [],
    ownedGameModifiers = [],
    bagTiles = [],
  } = useScrabbleGameContext();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 980,
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        padding: { xs: 1.2, md: 1.5 },
        boxShadow: (theme) => `0 10px 25px ${alpha(theme.palette.common.black, 0.16)}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: { xs: 'auto', lg: '100%' },
        maxHeight: { xs: '80svh', lg: 'none' },
        overflowY: { xs: 'auto', lg: 'hidden' },
      }}
    >
      {/* Header */}
      <Stack spacing={1.2} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Inventory2RoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Inventory
          </Typography>
        </Stack>

        {/* Summary stat row */}
        <Stack direction="row" spacing={1}>
          <StatCard
            Icon={GridOnRoundedIcon}
            count={heldBoardModifiers.length}
            label="Board Mods"
            getColor={getBoardColor}
          />
          <StatCard
            Icon={BoltRoundedIcon}
            count={ownedGameModifiers.length}
            label="Game Mods"
            getColor={getGameColor}
          />
          <StatCard
            Icon={ShoppingBagRoundedIcon}
            count={bagTiles.length}
            label="Tile Bag"
            getColor={getBagColor}
          />
        </Stack>
      </Stack>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 0.3 }}>
        <Stack spacing={1.2}>
          {/* Board Modifiers */}
          <SectionPanel
            Icon={GridOnRoundedIcon}
            title="Board Modifiers"
            count={heldBoardModifiers.length}
            getColor={getBoardColor}
          >
            {heldBoardModifiers.length === 0 ? (
              <EmptyState message="No board modifiers held. Buy one in the shop." />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 1,
                }}
              >
                {Object.values(
                  heldBoardModifiers.reduce((groups, modifier) => {
                    const key = modifier.boardSquareModifierId || modifier.id;
                    if (!groups[key]) groups[key] = { modifier, heldIds: [] };
                    groups[key].heldIds.push(modifier.heldId);
                    return groups;
                  }, {})
                ).map(({ modifier, heldIds }) => (
                  <ModifierCard
                    key={modifier.boardSquareModifierId || modifier.id}
                    option={modifier}
                    actionLabel="Place"
                    count={heldIds.length}
                    onAction={() => onPlaceModifier?.(heldIds[0])}
                  />
                ))}
              </Box>
            )}
          </SectionPanel>

          {/* Game Modifiers */}
          <SectionPanel
            Icon={BoltRoundedIcon}
            title="Game Modifiers"
            count={ownedGameModifiers.length}
            getColor={getGameColor}
          >
            {ownedGameModifiers.length === 0 ? (
              <EmptyState message="No game modifiers purchased yet." />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 1,
                }}
              >
                {ownedGameModifiers.map((modifier, index) => (
                  <ModifierCard
                    key={`${modifier.id}-${index}`}
                    option={modifier}
                  />
                ))}
              </Box>
            )}
          </SectionPanel>

          {/* Tile Bag */}
          <SectionPanel
            Icon={ShoppingBagRoundedIcon}
            title="Tile Bag"
            count={`${bagTiles.length} tile${bagTiles.length === 1 ? '' : 's'}`}
            getColor={getBagColor}
          >
            {bagTiles.length === 0 ? (
              <EmptyState message="Bag is empty." />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
                  gap: 1,
                }}
              >
                {[...bagTiles]
                  .sort((a, b) => String(a.letter).localeCompare(String(b.letter)))
                  .map((tile) => (
                    <Box
                      key={tile.id}
                      sx={{ width: '100%' }}
                      onMouseEnter={() => onInspectTile?.(tile)}
                      onMouseLeave={() => onInspectTile?.(null)}
                      onPointerDown={() => onInspectTile?.(tile)}
                    >
                      <LetterTile
                        tile={{ ...tile, points: LETTER_POINTS[tile.letter] || 0 }}
                        draggable={false}
                        compact={false}
                        variant="rack"
                      />
                    </Box>
                  ))}
              </Box>
            )}
          </SectionPanel>
        </Stack>
      </Box>
    </Box>
  );
}

export default ModifierPanel;
